import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation } from "@apollo/client";
import { GET_INVENTORY, REPORT_INVENTORY_LOSS } from "./graphql";
import Barcode from "react-barcode";
import * as htmlToImage from "html-to-image";
import download from "downloadjs";

const LOSS_REASONS = [
  { value: "DAMAGED", label: "Damaged" },
  { value: "LOST", label: "Lost" },
  { value: "EXPIRED", label: "Expired" },
  { value: "SPILLED", label: "Spilled" },
  { value: "OTHER", label: "Other" },
];

const ReportLossModal = ({ item, onClose, onSubmit, submitting }) => {
  const [reason, setReason] = useState("DAMAGED");
  const [description, setDescription] = useState("");

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Report Loss</h3>
          <p className="text-xs text-gray-500 mt-0.5">Item: {item.name}</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              {LOSS_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Any extra context..."
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(reason, description)}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Report Loss"}
          </button>
        </div>
      </div>
    </div>
  );
};

const InventoryLabel = ({ logoUrl, code }) => {
  return (
    <div className="print-label">
      {logoUrl && (
        <img
          src={logoUrl}
          alt="Logo"
          style={{ height: "5mm", marginBottom: "1mm", objectFit: "contain" }}
        />
      )}
      <div className="code">{code}</div>
      <div className="barcode-container">
        <Barcode
          value={code}
          height={22}
          width={1.2}
          fontSize={9}
          margin={0}
          displayValue={false}
        />
      </div>
    </div>
  );
};

const InventoryLabelPrint = React.forwardRef(
  ({ logoUrl, code, description }, ref) => {
    return (
      <div
        ref={ref}
        style={{
          width: "400px",
          padding: "12px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "white",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {logoUrl && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: "8px",
            }}
          >
            <img
              src={logoUrl}
              alt="Barbera"
              style={{
                height: "40px",
                maxWidth: "150px",
                objectFit: "contain",
              }}
            />
          </div>
        )}

        <div
          style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "4px" }}
        >
          {code}
        </div>

        {description && (
          <div style={{ fontSize: "12px", marginBottom: "8px", color: "#555" }}>
            {description}
          </div>
        )}

        <div>
          <Barcode value={code} height={40} fontSize={10} displayValue={true} />
        </div>
      </div>
    );
  },
);

const PendingInventoryDrawer = ({
  isOpen,
  onClose,
  categoryId,
  employeeId,
  categoryName,
  status = "Pending",
  date,
  onlyUsable,
}) => {
  const { data, loading, error } = useQuery(GET_INVENTORY, {
    variables: {
      data: {
        status: status,
        categoryId: categoryId || undefined,
        employeeId: employeeId === "ALL" ? undefined : employeeId,
        assignedDate: (date && employeeId !== "ALL") ? date : undefined,
        date: (date && employeeId === "ALL") ? date : undefined,
        onlyUsable: onlyUsable || undefined,
      },
    },
    skip: !isOpen,
    fetchPolicy: "network-only",
  });

  const inventoryList = data?.getInventory?.inventoryList || [];

  const [printData, setPrintData] = useState({});
  const printRef = useRef();

  const [reportLossItem, setReportLossItem] = useState(null);
  const [reportInventoryLoss, { loading: reportingLoss }] = useMutation(
    REPORT_INVENTORY_LOSS,
    { refetchQueries: ["GetInventory"] },
  );

  const handleReportLoss = async (reason, description) => {
    if (!reportLossItem) return;
    try {
      const res = await reportInventoryLoss({
        variables: {
          data: {
            inventoryIds: [reportLossItem.id],
            reason,
            description: description || undefined,
          },
        },
      });
      const result = res?.data?.reportInventoryLoss;
      if (result?.status === "SUCCESS") {
        setReportLossItem(null);
      } else {
        window.alert(result?.message || "Failed to report loss.");
      }
    } catch (e) {
      window.alert(e.message || "Failed to report loss.");
    }
  };

  const handlePrint = async (row) => {
    setPrintData(row);
    // Wait for state to apply and DOM to render the hidden label
    await new Promise((r) => requestAnimationFrame(r));
    if (printRef.current) {
      const dataUrl = await htmlToImage.toPng(printRef.current);
      download(dataUrl, `pending_label_${row.name}.png`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Global Print Area for 'Print All' (Portaled to body to allow multi-page printing) */}
      {createPortal(
        <div className="print-area">
          {inventoryList.map((item) => (
            <InventoryLabel
              key={item.id}
              logoUrl="https://api.barbera.in/uploads/1756537340509.jpg"
              code={item.name}
            />
          ))}
        </div>,
        document.body,
      )}

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-lg bg-white shadow-xl h-full flex flex-col transform transition-transform overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Inventory
            </h2>
            <p className="text-xs text-gray-500">
              {categoryName ? `Category: ${categoryName}` : "All Categories"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {inventoryList.length > 0 && (
              <button
                onClick={() => window.print()}
                className="text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg shadow-sm transition"
              >
                Print All (PDF)
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {loading ? (
            <p className="text-sm text-gray-500 text-center">
              Loading items...
            </p>
          ) : error ? (
            <p className="text-sm text-red-500 text-center">
              Failed to load pending inventory.
            </p>
          ) : inventoryList.length === 0 ? (
            <p className="text-sm text-gray-500 text-center">
              No pending inventory found.
            </p>
          ) : (
            <div className="space-y-3">
              {inventoryList.map((item) => {
                const qty = item.quantity || 1;
                const parts = item.parts || 0;
                const usage = item.usage || 0;
                const isScanReq = item.isScanRequired === true;

                let remainingQty = qty;
                let remainingParts = 0;

                if (!isScanReq && parts > 0) {
                  const completelyUsedQty = Math.floor(usage / parts);
                  remainingQty = Math.max(0, qty - completelyUsedQty);
                  remainingParts = Math.max(0, (qty * parts) - usage);
                }

                return (
                  <div
                    key={item.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center justify-between"
                  >
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        {item.name}
                        {item.quantity && item.quantity > 1 && (
                          <span className="bg-purple-100 text-purple-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                            Total Assigned: {item.quantity}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Category: {item.category}
                      </p>
                      {employeeId === "ALL" && item.employeeName && (
                        <p className="text-xs text-gray-500">
                          Employee: {item.employeeName}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        Assigned:{" "}
                        {item.assignedDate
                          ? new Date(
                            isNaN(Number(item.assignedDate))
                              ? item.assignedDate
                              : Number(item.assignedDate),
                          ).toLocaleDateString()
                          : "N/A"}
                      </p>
                      <p className="text-xs text-gray-700 font-medium mt-1">
                        Remaining Items: {remainingQty}
                      </p>
                      {!isScanReq && parts > 0 && (
                        <p className="text-xs text-gray-700 font-medium">
                          Remaining Parts: {remainingParts}
                        </p>
                      )}
                      <p className="text-xs font-semibold text-gray-700 mt-1">
                        Status: {item.status ? item.status.toUpperCase() : "UNKNOWN"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => handlePrint(item)}
                        className="text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition"
                      >
                        Print
                      </button>
                      <button
                        onClick={() => setReportLossItem(item)}
                        className="text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-lg transition"
                      >
                        Report Loss
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Hidden printable label for individual image downloads */}
        <div style={{ position: "absolute", left: "-9999px" }}>
          <InventoryLabelPrint
            ref={printRef}
            logoUrl="https://api.barbera.in/uploads/1756537340509.jpg"
            code={printData?.name || ""}
            description={printData?.description || ""}
          />
        </div>
      </div>

      <ReportLossModal
        item={reportLossItem}
        onClose={() => setReportLossItem(null)}
        onSubmit={handleReportLoss}
        submitting={reportingLoss}
      />
    </div>
  );
};

export default PendingInventoryDrawer;
